import { useEffect, useState } from 'react'

/**
 * Whether an element's text is actually being cut off.
 *
 * A clamped element reports a `scrollHeight` larger than the box it may
 * occupy, or a `scrollWidth` when it is cut to one line with `truncate`. That is the only way to know: the class says the text *may* be cut,
 * not that it is, and a note short enough to fit reads in full with the clamp
 * still applied.
 *
 * Measured by the observer rather than on mount, because the same note is cut
 * at one column width and whole at another, and because `ResizeObserver` fires
 * once when it starts observing. Where there is no `ResizeObserver` there is no
 * layout either, so nothing is reported as clamped.
 */
export function useIsClamped(): [(node: HTMLElement | null) => void, boolean] {
  const [element, setElement] = useState<HTMLElement | null>(null)
  const [clamped, setClamped] = useState(false)

  useEffect(() => {
    if (!element || typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(() => {
      // One pixel of tolerance: sub-pixel line heights make an untruncated
      // element report a scrollHeight a fraction larger than its client
      // height, which would claim every note was cut.
      setClamped(
        element.scrollHeight - element.clientHeight > 1 ||
          element.scrollWidth - element.clientWidth > 1
      )
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [element])

  return [setElement, clamped]
}
